/**
 * Resolves the navigation a user is entitled to: which side of the portal
 * they are on, the ordered base entries for that side, and the entries that
 * survive the visibility / permission filters.
 *
 * Extracted from Sidebar.tsx for CR-7058 so the route gate
 * (NavigationAccessGate) reads the same model the sidebar renders — a page
 * removed from the menu is exactly the page the gate refuses to open.
 */
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { canOpenAdminSetup } from '../../lib/adminSetupAccess';
import {
  buildNavigation, isHiddenByDashboardTab, PURCHASING_TAB_MENU_KEYS,
  type NavItem,
} from '../../config/navigation';
import { resolveNavPortalMode } from './navPortalMode';
import { isNavItemEnabledByConfig, type NavAccessContext } from './navigationAccess';

export interface SidebarNavigationModel {
  /** Ordered entries for the user's side of the portal, before any filtering. */
  baseNavigation: NavItem[];
  /**
   * Entries the route gate resolves a URL against: baseNavigation plus the
   * toggle-controlled pages that have no sidebar item (User Invitations).
   */
  gateNavigation: NavItem[];
  /** Entries the sidebar renders: config-enabled, flag-enabled and role-visible. */
  navigation: NavItem[];
  /**
   * CR-7732: whether this path is one a haulier is refused. The route gate
   * asks it too, so a surface missing from the sidebar cannot be reached by
   * typing its address.
   */
  isCarrierHiddenRoute: (path: string) => boolean;
  /** True when the supplier-side menu is rendered. */
  isSupplierSidebar: boolean;
  /** Helix platform account (platform HELIX / company_type helix). */
  isVCInternal: boolean;
  /** Neither Helix-internal nor a supplier user. */
  isCustomerLikeUser: boolean;
  /** May manage users: Helix internal, company admin or explicit capability. */
  canManageUsers: boolean;
  /** Module-style gate for one entry (tenant allowlist, admin toggle, module permission). */
  isNavItemEnabledByConfig: (item: NavItem) => boolean;
}

/**
 * Build the navigation model for the current user and view.
 *
 * Usage:
 * ```ts
 * const { navigation, isNavItemEnabledByConfig } = useSidebarNavigation();
 * ```
 *
 * @returns the base and filtered navigation plus the role flags the sidebar needs
 */
//: CR-7732: sidebar routes a haulier never has a use for. It moves other
//: people's goods, so it holds no stock of its own to report.
const CARRIER_HIDDEN_ROUTES = ['/stock-levels'];

export function useSidebarNavigation(): SidebarNavigationModel {
  const { user } = useAuth();
  const {
    isMenuItemVisible, isFeatureEnabled, featureFlags, isSpendAnalyticsMode, isComplianceMode,
    purchasingTabs,
    isCustomerPortalMode, isDashboardTabEnabled, mainMenuType, isLoading: menuSettingsLoading,
  } = useMenuSettings();
  const { hasModuleAccess } = useUserPermissions();
  const { portalMode, supportsDualMode } = usePortalMode();
  const { isPlatformOwner, enabledModules, selectedCompanyId, accessibleCompanies } = useView();

  // CR-5367 rework: "VC internal" must be gated on the user's actual company
  // affiliation (Helix platform), NOT on Django's is_staff / is_superuser
  // flags. Test data has set is_staff=True on customer/supplier accounts in
  // the past, and the sidebar was treating those accounts as Helix internals
  // and surfacing Platform Admin / Email Queue / Platform Analytics to
  // unauthorised tenants. Strictly check platform + company_type so the
  // gating cannot be bypassed by a stray Django staff flag.
  const isVCInternal = user?.platform === 'HELIX' || user?.company_type === 'helix';
  const isSupplierUserType = user?.company_type === 'supplier' || user?.platform === 'SUPPLIER';
  const isCustomerLikeUser = !isVCInternal && !isSupplierUserType;

  // Helix admins keep full access. For non-Helix users we use the
  // top-level is_company_admin / can_manage_users flags on the user object.
  const isCompanyAdmin = Boolean(user?.is_company_admin);
  const userCanManageUsers = Boolean(user?.can_manage_users);
  const canManageUsers = isVCInternal || isCompanyAdmin || userCanManageUsers;

  // Logistics users get a restricted single-page view
  const isLogisticsUser = user?.company_type === 'logistics';
  // CR-7732: and a supplier acting as a haulier loses the same stock surfaces
  // while keeping the rest of its supplier sidebar.
  const isCarrier = isLogisticsUser
    || (user?.company_type === 'supplier' && isFeatureEnabled('logistics_view'));
  const isAdminUser = !!user?.is_superuser || !!user?.is_staff;

  // The company selected in the view. For Helix-internal users this decides
  // the sidebar's side (see navPortalMode.ts). Should the selected id ever be
  // absent, treat it as a customer: the view-as selector offers customer
  // companies — suppliers are reached by impersonation.
  const viewedCompany = selectedCompanyId != null
    ? (accessibleCompanies.find((c) => c.id === selectedCompanyId) ?? { company_type: 'customer' })
    : null;
  const resolvedNavMode = resolveNavPortalMode({
    portalMode, isVCInternal, isAdminUser, supportsDualMode, mainMenuType, menuSettingsLoading, viewedCompany,
  });

  const getNavigationForUser = (): NavItem[] => {
    // Logistics users only see their carrier view of linked customers' call-offs
    if (isLogisticsUser) {
      return buildNavigation({ portalMode: 'customer', isSpendAnalytics: false, isLogistics: true });
    }

    // Which side of the portal to render. For Helix-internal users the company
    // being viewed decides; the platform main_menu_type default applies only
    // with nothing selected — navPortalMode.ts explains why.
    let navPortalMode: 'customer' | 'supplier' = resolvedNavMode;

    // Customer Portal: supplier-owner sees the customer admin experience
    const isOwnerInCustomerPortal = isCustomerPortalMode && (isPlatformOwner || isAdminUser);
    if (isOwnerInCustomerPortal) {
      navPortalMode = 'customer';
    }

    if (isSpendAnalyticsMode) {
      return buildNavigation({ portalMode: navPortalMode, isSpendAnalytics: true });
    }
    return buildNavigation({
      portalMode: navPortalMode,
      isSpendAnalytics: false,
      isCompliance: isComplianceMode,
      isCustomerPortalOwner: isOwnerInCustomerPortal,
    });
  };

  const baseNavigation = getNavigationForUser();
  // The supplier sidebar is rendered exactly when the resolved side is
  // supplier, so the visibility filtering agrees with the menu it filters.
  const isSupplierSidebar = resolvedNavMode === 'supplier';
  // CR-8282: every toggle now has a sidebar entry, so the gate resolves
  // against the sidebar list alone (inviting users follows User Management).
  const gateNavigation = baseNavigation;

  const accessContext: NavAccessContext = {
    isSupplierSidebar,
    enabledModules,
    isMenuItemVisible,
    hasModuleAccess,
    canOpenAdminSetup: canOpenAdminSetup(user, portalMode === 'supplier'),
  };
  const isEnabledByConfig = (item: NavItem): boolean => isNavItemEnabledByConfig(item, accessContext);

  // Filter navigation items based on visibility settings AND user module
  // permissions (shared with the route gate), then the sidebar-only rules.
  const navigation = baseNavigation.filter(item => {
    if (!isEnabledByConfig(item)) return false;
    if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;
    if (item.hiddenWhenFlagFalse && featureFlags[item.hiddenWhenFlagFalse] === false) return false;
    // CR-8613: an item that opens a Purchasing tab hides with that tab, so it
    // never lands on Purchasing's default tab instead.
    if (item.purchasingTab && (
      purchasingTabs[item.purchasingTab] === false
      || !isMenuItemVisible(PURCHASING_TAB_MENU_KEYS[item.purchasingTab])
    )) return false;
    // CR-7478: Supplier Onboarding follows the Compliance dashboard tab. The
    // /onboarding route enforces the same rule itself (SupplierAwareOnboarding
    // in routes.tsx), so this stays a sidebar-only rule like the flags above.
    if (isHiddenByDashboardTab(item, isDashboardTabEnabled, isComplianceMode)) return false;
    // CR-6084: admin-only items (audit / activity surfaces) are hidden from
    // plain users. Backend endpoints enforce the matching role gate — this
    // just keeps the surface out of nav for non-admins.
    if (item.adminOnly && !canManageUsers) return false;
    // CR-7732: a haulier moves other people's goods, so it has no parts of
    // its own to hold stock of. Supplier Stock Levels is hidden from both
    // kinds of carrier — the logistics company, and the supplier a customer
    // marked as its Logistics Provider, which keeps the supplier sidebar.
    if (isCarrier && CARRIER_HIDDEN_ROUTES.includes(item.href)) return false;
    return true;
  });

  return {
    baseNavigation,
    gateNavigation,
    navigation,
    isSupplierSidebar,
    isVCInternal,
    isCustomerLikeUser,
    canManageUsers,
    isNavItemEnabledByConfig: isEnabledByConfig,
    // CR-7732: the route gate asks this too, so a haulier cannot simply type
    // the address of a surface its sidebar refuses to offer.
    isCarrierHiddenRoute: (path: string): boolean =>
      isCarrier && CARRIER_HIDDEN_ROUTES.some(
        route => path === route || path.startsWith(`${route}/`),
      ),
  };
}
